import React, { useCallback, useEffect, useRef, useState } from 'react';

type SplashProps = {
  visible: boolean;
  onPlayNow: () => void;
  videoSrc?: string;
};

type Star = {
  x: number;
  y: number;
  radius: number;
  speed: number;
  angle: number;
};

const Starfield: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const starsRef = useRef<Star[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Fix TS strict null checks for nested closures
    const canvasEl: HTMLCanvasElement = canvas;
    const ctx2d: CanvasRenderingContext2D = ctx;

    const devicePixelRatioSafe = Math.min(typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1, 2);
    const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function seed(): void {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const count = Math.floor((width * height) / 2500);
      starsRef.current = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        radius: Math.random() * 1.5 + 0.2,
        speed: Math.random() * 0.7 + 0.2,
        angle: Math.random() * Math.PI * 2,
      }));
    }

    function resize(): void {
      const width = window.innerWidth;
      const height = window.innerHeight;
      canvasEl.width = Math.floor(width * devicePixelRatioSafe);
      canvasEl.height = Math.floor(height * devicePixelRatioSafe);
      canvasEl.style.width = `${width}px`;
      canvasEl.style.height = `${height}px`;
      ctx2d.setTransform(devicePixelRatioSafe, 0, 0, devicePixelRatioSafe, 0, 0);
      seed();
    }

    function paintFrame(): void {
      const width = window.innerWidth;
      const height = window.innerHeight;
      ctx2d.clearRect(0, 0, width, height);

      // Nebula glow
      const gradient = ctx2d.createRadialGradient(width * 0.7, height * 0.2, 0, width * 0.7, height * 0.2, Math.max(width, height));
      gradient.addColorStop(0, 'rgba(88,28,135,0.08)');
      gradient.addColorStop(0.5, 'rgba(2,132,199,0.06)');
      gradient.addColorStop(1, 'rgba(0,0,0,0)');
      ctx2d.fillStyle = gradient;
      ctx2d.fillRect(0, 0, width, height);

      for (let i = 0; i < starsRef.current.length; i += 1) {
        const star = starsRef.current[i];
        star.y += star.speed;
        star.angle += 0.02 + star.speed * 0.01;
        if (star.y > height) {
          star.y = -2;
          star.x = Math.random() * width;
        }
        const twinkle = 0.5 + Math.sin(star.angle) * 0.5;
        ctx2d.globalAlpha = 0.4 + twinkle * 0.6;
        ctx2d.beginPath();
        ctx2d.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        ctx2d.fillStyle = 'white';
        ctx2d.fill();
      }
      ctx2d.globalAlpha = 1;

      rafRef.current = requestAnimationFrame(paintFrame);
    }

    resize();
    if (!prefersReducedMotion) {
      paintFrame();
    } else {
      // Just draw static background once
      const width = window.innerWidth;
      const height = window.innerHeight;
      ctx2d.clearRect(0, 0, width, height);
      const gradient = ctx2d.createRadialGradient(width * 0.7, height * 0.2, 0, width * 0.7, height * 0.2, Math.max(width, height));
      gradient.addColorStop(0, 'rgba(88,28,135,0.08)');
      gradient.addColorStop(0.5, 'rgba(2,132,199,0.06)');
      gradient.addColorStop(1, 'rgba(0,0,0,0)');
      ctx2d.fillStyle = gradient;
      ctx2d.fillRect(0, 0, width, height);
      for (let i = 0; i < starsRef.current.length; i += 1) {
        const star = starsRef.current[i];
        ctx2d.beginPath();
        ctx2d.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        ctx2d.fillStyle = 'white';
        ctx2d.globalAlpha = 0.8;
        ctx2d.fill();
      }
      ctx2d.globalAlpha = 1;
    }

    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return <canvas ref={canvasRef} className="splash-canvas" aria-hidden="true" />;
};

const Splash: React.FC<SplashProps> = ({ visible, onPlayNow, videoSrc }) => {
  const [showVideo, setShowVideo] = useState<boolean>(true);
  const [muted] = useState<boolean>(true); // keep autoplay-friendly
  const handlePlay = useCallback(() => {
    onPlayNow();
  }, [onPlayNow]);

  useEffect(() => {
    // Respect reduced motion: fall back to static starfield
    try {
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) setShowVideo(false);
    } catch {}

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        handlePlay();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handlePlay]);

  if (!visible) return null;

  return (
    <div className="splash-root" role="dialog" aria-modal="true" aria-label="Pax Imperialis">
      {showVideo ? (
        <video
          className="splash-video"
          src={(videoSrc && videoSrc.trim()) ? videoSrc : '/splash.mp4'}
          autoPlay
          muted={muted}
          loop
          playsInline
          preload="none"
          onError={() => setShowVideo(false)}
        />
      ) : (
        <Starfield />
      )}

      {/* Gradient planets */}
      <div className="splash-planet p1" aria-hidden="true" />
      <div className="splash-planet p2" aria-hidden="true" />

      {/* Glare + vignette */}
      <div className="splash-glare" aria-hidden="true">
        <div className="g g1" />
        <div className="g g2" />
      </div>

      {/* Center content */}
      <main className="splash-content" role="document">
        <h1 className="splash-title">
          Pax <span className="accent">Imperialis</span>
        </h1>
        <p className="splash-tagline">Forge order among the stars.</p>
        <div className="splash-actions">
          <button className="splash-cta light" onClick={handlePlay} aria-label="Play Now" autoFocus>
            <span>Play Now</span>
            <span className="cta-icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M4 12a8 8 0 1 1 16 0 8 8 0 0 1-16 0Zm6.5-3.5 5 3-5 3v-6Z" />
              </svg>
            </span>
          </button>
        </div>
        <div className="splash-keys" aria-hidden="true">Press Enter or Space</div>
      </main>

      <footer className="splash-footer" aria-hidden="true">© {new Date().getFullYear()} Pax Imperialis</footer>
    </div>
  );
};

export default Splash;


